// Per use_case, which provider_type values BE actually supports today.
//
// BE-side vendor lock: transcription_resolver and image_vision_runner
// still construct a Gemini SDK client (`geminiac.Client`) directly and
// reject other provider types. Without an FE filter the LLM workspace
// happily lets admins bind e.g. OpenRouter to transcription and the BE
// then 422s with no obvious recovery hint.
//
// `chat` and `extract_kg` share one BE router (kgextraction
// MultiVendorChatLLM) that dispatches Gemini natively + every
// OpenAI-compatible slug (openai, openrouter, groq, deepseek, mistral,
// xai, openai_compat) via the openaiac client. Anthropic / voyage are
// still rejected at the router; surfacing that as an FE constraint
// would mean enumerating every supported slug here, which churns
// every time BE adds a vendor. We instead keep the slot open and
// rely on the BE 422 + humanized error slug for the long-tail.
//
// Empty array = no constraint (all enabled provider types accepted).
//
// When BE multi-vendor lands for transcription/vision (Phase B/C),
// open the corresponding slot here without an FE deploy lockstep.
import type { UseCase } from "@/types/llm-model";

export const SUPPORTED_COMPLETION_PROVIDER_TYPES = [
  "gemini",
  "openai",
  "openrouter",
  "groq",
  "deepseek",
  "mistral",
  "xai",
  "openai_compat",
  "bailian",
  "dashscope",
  "minimax",
  "byteplus_coding",
] as const;

export const USE_CASE_PROVIDER_TYPE_ALLOWLIST: Record<UseCase, readonly string[]> = {
  transcription: ["gemini"],
  transcription_refine: SUPPORTED_COMPLETION_PROVIDER_TYPES,
  vision: ["gemini"],
  extract_kg: SUPPORTED_COMPLETION_PROVIDER_TYPES,
  chat: SUPPORTED_COMPLETION_PROVIDER_TYPES,
  embedding: [],
} as const;

/** True when the use_case has any provider-type constraint. */
export function hasProviderTypeConstraint(useCase: UseCase): boolean {
  return USE_CASE_PROVIDER_TYPE_ALLOWLIST[useCase].length > 0;
}

/** Human-friendly constraint hint, e.g. "Gemini" / "Gemini, OpenAI".
 *  Returns empty string when the use_case is unconstrained. */
export function constraintLabel(useCase: UseCase): string {
  const allowed = USE_CASE_PROVIDER_TYPE_ALLOWLIST[useCase];
  if (allowed.length === 0) return "";
  return allowed
    .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
    .join(", ");
}
